/**
 * Aanmaken en wijzigen van een regeling.
 *
 * Het veld dat hier het meeste toe doet is `bron_gecontroleerd_op`: de datum
 * waarop iemand de RVO-pagina daadwerkelijk heeft nagelopen. Die is met de hand
 * te zetten en staat naast een knop "vandaag", want zonder handmatige
 * bediening zou hij nooit kloppen.
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, Info } from 'lucide-react';
import type { SubsidieRegeling } from '@/hooks/useSubsidieRegelingen';
import {
  REGELING_STATUSSEN,
  REGELING_TYPES,
  leesBronGecontroleerdOp,
  type RegelingInvoer,
} from '@/hooks/useSubsidieBeheer';
import { REDEN } from '@/hooks/subsidie-schrijfrecht';
import { bronVersheid, vandaagAlsDatum } from './bron-versheid';

/** `null` bij een lege invoer, anders het getal. Lege tekst is geen 0. */
function alsGetal(ruw: string): number | null {
  const tekst = ruw.trim().replace(',', '.');
  if (tekst === '') return null;
  const getal = Number(tekst);
  return Number.isFinite(getal) ? getal : null;
}

function alsTekstOfNull(ruw: string): string | null {
  const tekst = ruw.trim();
  return tekst === '' ? null : tekst;
}

/** Een `timestamptz` uit de database terug naar `YYYY-MM-DD` voor een date-input. */
function alsDatumInvoer(waarde: string | null): string {
  if (!waarde) return '';
  return waarde.slice(0, 10);
}

export function RegelingDialog({
  open,
  onOpenChange,
  regeling,
  jaar,
  magIdentiteit,
  magRekengrondslag,
  onBewaar,
  isBezig,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `null` = nieuwe regeling. */
  regeling: SubsidieRegeling | null;
  jaar: number;
  /**
   * Mag deze gebruiker de IDENTITEIT van een bestaande regeling wijzigen — de
   * code en het jaar? Alleen admin en super_admin (083). Elk ander veld staat
   * voor iedereen open: wat een wijziging met de lopende dossiers doet, toont
   * het paneel vóór het opslaan.
   *
   * Bij een NIEUWE regeling geldt dit slot niet; een INSERT heeft geen OLD om
   * tegen te vergelijken. Het veld gaat dan ook niet uit.
   *
   * Uitgeschakeld en niet verborgen: een veld dat verdwijnt leert niemand dat
   * het bestaat en aan iemand anders toekomt.
   */
  magIdentiteit: boolean;
  /**
   * Mag deze gebruiker de rekengrondslag wijzigen — het soort, de
   * budgetteller, en de drie geld-grenzen?
   *
   * Migratie 083 geeft die vrij; de whitelist van 070 die VANDAAG in de
   * database staat doet dat niet. Zolang 083 niet is toegepast staan ze dus uit
   * voor een crm_user, anders belooft dit formulier iets dat bij opslaan met
   * 42501 terugkomt. Voor een admin staan ze altijd open (het kolomslot van 070
   * laat admin en super_admin door).
   */
  magRekengrondslag: boolean;
  onBewaar: (invoer: { id?: string; velden: RegelingInvoer }) => void;
  isBezig: boolean;
}) {
  /** Het identiteitsslot geldt alleen bij een BESTAANDE regeling. */
  const identiteitOpSlot = regeling !== null && !magIdentiteit;
  /** Zelfde vorm: het slot geldt alleen bij een BESTAANDE regeling. */
  const grondslagOpSlot = regeling !== null && !magRekengrondslag;

  const [code, setCode] = useState('');
  const [naam, setNaam] = useState('');
  const [korteNaam, setKorteNaam] = useState('');
  const [type, setType] = useState<SubsidieRegeling['type']>('subsidie');
  const [status, setStatus] = useState<SubsidieRegeling['status']>('open');
  const [aanvraagOpen, setAanvraagOpen] = useState('');
  const [aanvraagSluit, setAanvraagSluit] = useState('');
  const [conceptVanaf, setConceptVanaf] = useState('');
  const [budgetTotaal, setBudgetTotaal] = useState('');
  const [budgetRestant, setBudgetRestant] = useState('');
  const [budgetToelichting, setBudgetToelichting] = useState('');
  const [maxPerAanvrager, setMaxPerAanvrager] = useState('');
  const [minBedrag, setMinBedrag] = useState('');
  const [percentageMax, setPercentageMax] = useState('');
  const [rvoUrl, setRvoUrl] = useState('');
  const [samenvatting, setSamenvatting] = useState('');
  const [opmerkingen, setOpmerkingen] = useState('');
  const [gecontroleerdOp, setGecontroleerdOp] = useState('');

  useEffect(() => {
    if (!open) return;
    setCode(regeling?.code ?? '');
    setNaam(regeling?.naam ?? '');
    setKorteNaam(regeling?.korte_naam ?? '');
    setType(regeling?.type ?? 'subsidie');
    setStatus(regeling?.status ?? 'open');
    setAanvraagOpen(alsDatumInvoer(regeling?.aanvraag_open ?? null));
    setAanvraagSluit(alsDatumInvoer(regeling?.aanvraag_sluit ?? null));
    setConceptVanaf(alsDatumInvoer(regeling?.concept_vanaf ?? null));
    setBudgetTotaal(regeling?.budget_totaal != null ? String(regeling.budget_totaal) : '');
    setBudgetRestant(regeling?.budget_restant != null ? String(regeling.budget_restant) : '');
    setBudgetToelichting(regeling?.budget_toelichting ?? '');
    setMaxPerAanvrager(regeling?.max_subsidie_per_aanvrager != null ? String(regeling.max_subsidie_per_aanvrager) : '');
    setMinBedrag(regeling?.min_subsidiebedrag != null ? String(regeling.min_subsidiebedrag) : '');
    setPercentageMax(regeling?.subsidie_percentage_max != null ? String(regeling.subsidie_percentage_max) : '');
    setRvoUrl(regeling?.rvo_url ?? '');
    setSamenvatting(regeling?.samenvatting ?? '');
    setOpmerkingen(regeling?.opmerkingen ?? '');
    setGecontroleerdOp(leesBronGecontroleerdOp(regeling) ?? '');
  }, [open, regeling]);

  const fouten: string[] = [];
  if (code.trim() === '') fouten.push('Een code is verplicht; hij is samen met het jaar uniek (sub_regelingen_code_jaar_key).');
  if (naam.trim() === '') fouten.push('Een volledige naam is verplicht.');
  if (korteNaam.trim() === '') fouten.push('Een korte naam is verplicht; die staat op de kaarten en in het dashboard.');
  const percentage = alsGetal(percentageMax);
  if (percentageMax.trim() !== '' && percentage === null) fouten.push('Het maximumpercentage is geen getal.');
  if (percentage !== null && (percentage < 0 || percentage > 1)) {
    fouten.push('Het maximumpercentage is een fractie tussen 0 en 1 (0,4 = 40%); zo leest de rekenkern hem ook.');
  }

  const versheid = bronVersheid(gecontroleerdOp === '' ? null : gecontroleerdOp, new Date());

  /**
   * Bij een NIEUWE regeling staat alles open: wie mag aanmaken is per definitie
   * admin (er is geen INSERT-policy voor crm_user, 070). Het slot geldt dus
   * alleen bij het wijzigen van een bestaande rij, precies zoals de trigger:
   * die vergelijkt OLD met NEW en heeft bij een INSERT niets om te vergelijken.
   */

  function opslaan() {
    if (fouten.length > 0 || isBezig) return;
    onBewaar({
      id: regeling?.id,
      velden: {
        code: code.trim(),
        jaar,
        naam: naam.trim(),
        korte_naam: korteNaam.trim(),
        type,
        status,
        aanvraag_open: alsTekstOfNull(aanvraagOpen),
        aanvraag_sluit: alsTekstOfNull(aanvraagSluit),
        concept_vanaf: alsTekstOfNull(conceptVanaf),
        budget_totaal: alsGetal(budgetTotaal),
        budget_restant: alsGetal(budgetRestant),
        budget_toelichting: alsTekstOfNull(budgetToelichting),
        max_subsidie_per_aanvrager: alsGetal(maxPerAanvrager),
        min_subsidiebedrag: alsGetal(minBedrag),
        subsidie_percentage_max: percentage,
        rvo_url: alsTekstOfNull(rvoUrl),
        opmerkingen: alsTekstOfNull(opmerkingen),
        samenvatting: alsTekstOfNull(samenvatting),
        bron_gecontroleerd_op: alsTekstOfNull(gecontroleerdOp),
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{regeling ? `${regeling.korte_naam} wijzigen` : `Nieuwe regeling ${jaar}`}</DialogTitle>
          <DialogDescription>
            Bedragen, data en voorwaarden komen van RVO. Leg bij elke wijziging vast wanneer je die bron
            gecontroleerd hebt.
          </DialogDescription>
        </DialogHeader>

        {grondslagOpSlot && (
          <p className="flex items-start gap-2 rounded-md bg-muted p-3 text-xs text-muted-foreground">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>
              <strong className="font-medium">Soort en de drie geld-grenzen staan uit.</strong>{' '}
              {REDEN.rekengrondslag070}
            </span>
          </p>
        )}

        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Code</Label>
              <Input
                value={code}
                onChange={e => setCode(e.target.value)}
                placeholder="bijv. SPULA"
                disabled={identiteitOpSlot}
                title={identiteitOpSlot ? REDEN.identiteitRegeling : undefined}
              />
            </div>
            <div className="space-y-2">
              <Label>Korte naam</Label>
              <Input value={korteNaam} onChange={e => setKorteNaam(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Jaar</Label>
              <Input value={jaar} disabled />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Volledige naam</Label>
            <Input value={naam} onChange={e => setNaam(e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Soort</Label>
              <Select value={type} onValueChange={v => setType(v as SubsidieRegeling['type'])} disabled={grondslagOpSlot}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REGELING_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={status} onValueChange={v => setStatus(v as SubsidieRegeling['status'])}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REGELING_STATUSSEN.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Aanvraag open</Label>
              <Input type="date" value={aanvraagOpen} onChange={e => setAanvraagOpen(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Aanvraag sluit</Label>
              <Input type="date" value={aanvraagSluit} onChange={e => setAanvraagSluit(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Concept vanaf</Label>
              <Input type="date" value={conceptVanaf} onChange={e => setConceptVanaf(e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Budget totaal (EUR)</Label>
              <Input value={budgetTotaal} onChange={e => setBudgetTotaal(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Budget restant (EUR)</Label>
              <Input value={budgetRestant} onChange={e => setBudgetRestant(e.target.value)} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Toelichting op het budget</Label>
            <Textarea value={budgetToelichting} onChange={e => setBudgetToelichting(e.target.value)} rows={2} />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Max. per aanvrager (EUR)</Label>
              <Input value={maxPerAanvrager} onChange={e => setMaxPerAanvrager(e.target.value)} disabled={grondslagOpSlot} title={grondslagOpSlot ? REDEN.rekengrondslag070 : undefined} />
            </div>
            <div className="space-y-2">
              <Label>Minimum (EUR)</Label>
              <Input value={minBedrag} onChange={e => setMinBedrag(e.target.value)} disabled={grondslagOpSlot} title={grondslagOpSlot ? REDEN.rekengrondslag070 : undefined} />
            </div>
            <div className="space-y-2">
              <Label>Max. percentage (0-1)</Label>
              <Input value={percentageMax} onChange={e => setPercentageMax(e.target.value)} disabled={grondslagOpSlot} title={grondslagOpSlot ? REDEN.rekengrondslag070 : undefined} placeholder="0.4" />
            </div>
          </div>

          <p className="flex items-start gap-2 rounded-md bg-muted p-3 text-xs text-muted-foreground">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>
              Na "Opslaan" zie je eerst wat deze wijziging met de lopende dossiers doet — welk dossier van
              welk bedrag naar welk bedrag gaat. Verschuift er niets, dan slaat hij meteen op.
            </span>
            </p>

          <div className="space-y-2">
            <Label>RVO-pagina</Label>
            <Input value={rvoUrl} onChange={e => setRvoUrl(e.target.value)} placeholder="https://www.rvo.nl/..." />
          </div>

          <div className="space-y-2">
            <Label>Samenvatting</Label>
            <Textarea value={samenvatting} onChange={e => setSamenvatting(e.target.value)} rows={2} />
          </div>

          <div className="space-y-2">
            <Label>Opmerkingen</Label>
            <Textarea value={opmerkingen} onChange={e => setOpmerkingen(e.target.value)} rows={2} />
          </div>

          <div className="space-y-2 rounded-md border p-3">
            <Label>Bron gecontroleerd op</Label>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                className="max-w-[200px]"
                value={gecontroleerdOp}
                onChange={e => setGecontroleerdOp(e.target.value)}
              />
              <Button type="button" variant="outline" size="sm" onClick={() => setGecontroleerdOp(vandaagAlsDatum())}>
                Vandaag
              </Button>
              <span className={`text-sm ${versheid.status === 'vers' ? 'text-muted-foreground' : 'text-amber-700'}`}>
                {versheid.tekst}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              De dag waarop je de RVO-pagina hebt nagelopen — niet de dag waarop je hier iets wijzigt.
              Zonder datum geldt de regeling als ongeverifieerd.
            </p>
          </div>

          {fouten.map(f => (
            <div key={f} className="flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-800">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{f}</span>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuleren</Button>
          <Button onClick={opslaan} disabled={fouten.length > 0 || isBezig}>Opslaan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
